import { useState } from "react";
import type { AskResult, SparqlResults } from "@nl2sparql/core";
import { Panel } from "../../components/Panel.tsx";
import { CopyButton } from "../../components/CopyButton.tsx";

function Query({ sparql }: { sparql: string }) {
  return (
    <pre
      className="overflow-x-auto rounded-lg p-3 text-xs leading-relaxed"
      style={{ background: "var(--nl2s-code-bg)", color: "var(--nl2s-text)" }}
    >
      <code>{sparql}</code>
    </pre>
  );
}

function ResultsGrid({ res }: { res: SparqlResults }) {
  if (res.boolean !== undefined) {
    return <div className="text-sm font-semibold" style={{ color: "var(--nl2s-text)" }}>{String(res.boolean)}</div>;
  }
  if (res.rows.length === 0) {
    return <div className="text-xs" style={{ color: "var(--nl2s-text-muted)" }}>No results.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {res.head.map((h) => (
              <th key={h} className="border-b px-2 py-1 text-left font-mono" style={{ borderColor: "var(--nl2s-border)", color: "var(--nl2s-text-muted)" }}>
                ?{h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {res.rows.slice(0, 50).map((row, i) => (
            <tr key={i}>
              {res.head.map((h) => {
                const b = row[h];
                return (
                  <td key={h} className="border-b px-2 py-1 align-top" style={{ borderColor: "var(--nl2s-border)", color: "var(--nl2s-text)" }}>
                    {b?.type === "uri" ? (
                      <a href={b.value} target="_blank" rel="noreferrer" style={{ color: "var(--nl2s-accent)" }}>
                        {b.value}
                      </a>
                    ) : (
                      b?.value ?? ""
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SparqlPanel({
  result,
  onRun,
}: {
  result: AskResult;
  onRun?: (sparql: string) => Promise<SparqlResults>;
}) {
  const chosen = result.chosenIndex !== undefined ? result.attempts[result.chosenIndex] : result.attempts[0];
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<SparqlResults | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  if (result.attempts.length === 0 || !chosen) return null;

  const startEditing = () => {
    setDraft(chosen.sparql);
    setEditing(true);
    setRunResult(null);
    setRunError(null);
  };

  const run = async () => {
    if (!onRun) return;
    setRunning(true);
    setRunError(null);
    try {
      setRunResult(await onRun(draft));
    } catch (err) {
      setRunError(err instanceof Error ? err.message : String(err));
      setRunResult(null);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Panel
      title="Generated SPARQL"
      subtitle={`Dialect: ${result.sourceId ?? "wikidata"}`}
      right={
        <div className="flex items-center gap-2">
          {onRun ? (
            <button type="button" onClick={editing ? () => setEditing(false) : startEditing} className="rounded-md px-2 py-1 text-xs font-medium" style={{ background: "var(--nl2s-surface-raised)", color: "var(--nl2s-text-muted)" }}>
              {editing ? "Close editor" : "Edit & run"}
            </button>
          ) : null}
          <CopyButton text={chosen.sparql} />
        </div>
      }
    >
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={Math.min(18, draft.split("\n").length + 1)}
            className="w-full rounded-lg p-3 font-mono text-xs"
            style={{ background: "var(--nl2s-code-bg)", color: "var(--nl2s-text)", border: "1px solid var(--nl2s-border-strong)" }}
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={run} disabled={running} className="rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50" style={{ background: "var(--nl2s-accent)", color: "var(--nl2s-accent-contrast)" }}>
              {running ? "Running…" : "Run"}
            </button>
            <span className="text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
              Runs against the active SPARQL endpoint. Try adding a{" "}
              <code>SERVICE &lt;endpoint&gt; {"{ … }"}</code> block to federate.
            </span>
          </div>
          {runError ? (
            <div className="rounded-md p-2 text-xs" style={{ background: "var(--nl2s-accent-soft)", color: "var(--nl2s-danger)" }}>
              {runError}
            </div>
          ) : null}
          {runResult ? (
            <div className="mt-1">
              <div className="mb-1 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
                {runResult.rows.length} row{runResult.rows.length === 1 ? "" : "s"} · {runResult.elapsedMs}ms
              </div>
              <ResultsGrid res={runResult} />
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <Query sparql={chosen.sparql} />
          {result.attempts.length > 1 ? (
            <button type="button" onClick={() => setShowAll((s) => !s)} className="mt-3 text-xs font-medium" style={{ color: "var(--nl2s-accent)" }}>
              {showAll ? "Hide" : `Show all ${result.attempts.length} candidate queries`}
            </button>
          ) : null}
          {showAll ? (
            <div className="mt-3 space-y-3">
              {result.attempts.map((a, i) => (
                <div key={i}>
                  <div className="mb-1 flex items-center gap-2 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
                    <span className="font-mono">{a.strategy}</span>
                    <span>·</span>
                    <span>{a.error ? `error: ${a.error}` : `${a.rowCount} rows`}</span>
                    {i === result.chosenIndex ? (
                      <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: "var(--nl2s-accent-soft)", color: "var(--nl2s-accent)" }}>
                        chosen
                      </span>
                    ) : null}
                  </div>
                  <Query sparql={a.sparql} />
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}
